import { useEffect, useRef, useState } from "preact/hooks";
import { z } from "zod/v4";
import { api, apiClient } from "../../constants.ts";
import { ArrowRightIcon, Spinner } from "./components.tsx";
import { usePageMeta } from "./pageMeta.ts";
import { PricingSection } from "./pricing.tsx";
import { agencySocialMeta } from "./socialMeta.ts";
import { talkToSkillRoute } from "./talkToSkill.tsx";

export const templatesRoute = "/templates";
export const agencyRoute = "/agency";

type AgencyRoster = z.infer<(typeof api)["list-agency-agents"]["output"]>;
type AgencyDivision = AgencyRoster["divisions"][number];
type AgencyAgent = AgencyDivision["agents"][number];

const agentLink = ({ url }: AgencyAgent) =>
  `${talkToSkillRoute}?url=${encodeURIComponent(url)}`;

const loadedDivisionImages = new Set<string>();

const ThemedDivisionImage = (
  { id, alt, className }: { id: string; alt: string; className?: string },
) => {
  const [loaded, setLoaded] = useState(() => loadedDivisionImages.has(id));
  const lightRef = useRef<HTMLImageElement>(null);
  const darkRef = useRef<HTMLImageElement>(null);

  useEffect(() => {
    if (
      loadedDivisionImages.has(id) ||
      lightRef.current?.complete ||
      darkRef.current?.complete
    ) {
      loadedDivisionImages.add(id);
      setLoaded(true);
    }
  }, [id]);

  const handleLoad = () => {
    loadedDivisionImages.add(id);
    setLoaded(true);
  };

  return (
    <div
      className={`relative overflow-hidden bg-surface-overlay-light dark:bg-surface-overlay ${
        className ?? ""
      }`}
    >
      {!loaded && (
        <div className="absolute inset-0 bg-surface-overlay-light dark:bg-surface-overlay animate-pulse" />
      )}
      <img
        ref={lightRef}
        src={`/agency/${id}-light.webp`}
        alt={alt}
        loading="lazy"
        decoding="async"
        onLoad={handleLoad}
        className="dark:hidden w-full h-full object-cover"
      />
      <img
        ref={darkRef}
        src={`/agency/${id}-dark.webp`}
        alt={alt}
        loading="lazy"
        decoding="async"
        onLoad={handleLoad}
        className="hidden dark:block w-full h-full object-cover"
      />
    </div>
  );
};

const AgentCard = (
  { agent }: { agent: AgencyAgent },
) => (
  <a
    href={agentLink(agent)}
    className="group flex flex-col justify-between rounded-xl border border-border-subtle-light dark:border-border-subtle bg-surface-raised-light dark:bg-surface-raised p-5 shadow-xs transition-all duration-200 hover:-translate-y-1 hover:shadow-md hover:border-accent/40 text-left"
  >
    <div>
      <h3 className="font-semibold text-text-primary-light dark:text-text-primary group-hover:text-accent-light dark:group-hover:text-accent transition-colors leading-snug mb-2">
        {agent.name}
      </h3>
      {agent.description && (
        <p className="text-sm text-text-secondary-light dark:text-text-secondary leading-relaxed line-clamp-3">
          {agent.description}
        </p>
      )}
    </div>
    <div className="flex items-center justify-between pt-4 mt-3 border-t border-border-subtle-light/60 dark:border-border-subtle/60">
      <span className="text-xs font-semibold uppercase tracking-wider text-accent-light dark:text-accent group-hover:underline">
        Create agent
      </span>
      <ArrowRightIcon className="w-3.5 h-3.5 text-text-tertiary group-hover:text-accent transition-transform group-hover:translate-x-0.5 duration-150" />
    </div>
  </a>
);

const DivisionSection = ({ division }: { division: AgencyDivision }) => (
  <section className="w-full max-w-6xl mx-auto px-4 py-8">
    <div className="rounded-2xl border border-border-subtle-light dark:border-border-subtle bg-surface-raised-light dark:bg-surface-raised overflow-hidden shadow-sm mb-6">
      <div className="flex flex-col md:flex-row items-center justify-between gap-6 p-6">
        <div className="flex-1 text-left">
          <div className="flex items-center gap-2.5 mb-2">
            <span className="text-xs font-semibold uppercase tracking-wider px-2.5 py-0.5 rounded-full bg-surface-overlay-light dark:bg-surface-overlay text-text-secondary-light dark:text-text-secondary border border-border-subtle-light dark:border-border-subtle">
              {division.label} Division
            </span>
            <span className="text-xs text-text-tertiary-light dark:text-text-tertiary">
              {division.agents.length} specialists
            </span>
          </div>
          <h2 className="font-display text-2xl md:text-3xl font-bold text-text-primary-light dark:text-text-primary">
            {division.label}
          </h2>
          <p className="text-sm text-text-secondary-light dark:text-text-secondary mt-1 max-w-xl">
            Autonomous specialists configured for {division.label.toLowerCase()}
            {" "}
            workflows, tools, and multi-channel deployment.
          </p>
        </div>
        <div className="w-full md:w-64 aspect-[16/9] md:aspect-[16/10] rounded-xl overflow-hidden border border-border-subtle-light dark:border-border-subtle flex-shrink-0 shadow-xs">
          <ThemedDivisionImage
            id={division.id}
            alt={division.label}
            className="w-full h-full"
          />
        </div>
      </div>
    </div>
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {division.agents.map((agent) => (
        <AgentCard key={agent.url} agent={agent} />
      ))}
    </div>
  </section>
);

const Hero = ({ agentCount }: { agentCount?: number }) => (
  <section className="w-full max-w-3xl mx-auto px-4 pt-16 pb-8 text-center">
    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-widest bg-surface-overlay-light dark:bg-surface-overlay text-text-tertiary-light dark:text-text-tertiary border border-border-subtle-light dark:border-border-subtle mb-4">
      <span>prompt2bot</span>
      <span>×</span>
      <span>agency-agents</span>
    </div>
    <h1 className="font-display text-4xl sm:text-5xl font-bold mb-6 text-text-primary-light dark:text-text-primary">
      Hire a whole AI agency
    </h1>
    <p className="text-lg text-text-secondary-light dark:text-text-secondary leading-relaxed mb-4">
      {agentCount ? `${agentCount} specialists` : "Specialists"}{" "}
      — engineers, designers, marketers, strategists — each one click away. Pick
      a role and instantly get a personal AI agent with that exact skill, ready
      to chat on WhatsApp, Telegram, or the web.
    </p>
    <p className="text-sm text-text-tertiary-light dark:text-text-tertiary">
      Roster syncs automatically from the{" "}
      <a
        href="https://github.com/msitarzewski/agency-agents"
        target="_blank"
        rel="noopener noreferrer"
        className="underline hover:text-accent font-medium"
      >
        agency-agents
      </a>{" "}
      open-source repo.
    </p>
  </section>
);

export const Agency = () => {
  usePageMeta({
    title: agencySocialMeta.title,
    description: agencySocialMeta.description,
    image: agencySocialMeta.image,
    twitterCard: "summary_large_image",
  });
  const [roster, setRoster] = useState<AgencyRoster | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    apiClient({ endpoint: "list-agency-agents", payload: {} })
      .then(setRoster)
      .catch((err: Error) => setError(err.message));
  }, []);

  const agentCount = roster?.divisions.reduce(
    (total, { agents }) => total + agents.length,
    0,
  );

  return (
    <main className="flex flex-col flex-1 items-center pb-16">
      <Hero agentCount={agentCount} />
      {error && <p className="text-rose-500 font-medium">{error}</p>}
      {!roster && !error && (
        <div className="flex flex-col flex-1 items-center justify-center gap-3 py-16 w-full">
          <Spinner className="h-10 w-10 text-accent" />
          <p className="text-text-secondary-light dark:text-text-secondary font-medium">
            Fetching the agency roster...
          </p>
        </div>
      )}
      {roster?.divisions.map((division) => (
        <DivisionSection key={division.id} division={division} />
      ))}
      <section className="w-full max-w-6xl mx-auto px-4 py-16 border-t border-border-subtle-light dark:border-border-subtle">
        <PricingSection />
      </section>
    </main>
  );
};
